import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Minus, Plus, ShieldCheck, Truck, ShoppingBag } from "lucide-react";
import { api, cardProduct, errMsg, inr, offerLabel, productImage, resolvePricing, shippingInfo, totalStock, FALLBACK_IMG } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";

const pretty = (s) => (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function ProductPage() {
  const { productId } = useParams();
  const { catalog, requireAuth, addToCart, toggleWishlist, wishlist, pincodeInfo } = useStore();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    setP(null); setNotFound(false); setQty(1); setImgIdx(0);
    api.get(`/ecom/catalog/products/${productId}`, { params: { ...(pincodeInfo?.store_id && { store_id: pincodeInfo.store_id }) } })
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true));
  }, [productId, pincodeInfo?.store_id]);

  const brandMap = useMemo(
    () => Object.fromEntries(catalog.brands.map((b) => [b.id, b.name])),
    [catalog.brands]
  );

  const pName = p ? pretty(p.name) : "";
  const pBrand = p ? pretty(brandMap[p.brand_id] || "") : "";
  const pricing = p ? resolvePricing(p) : { price: 0, mrp: 0, off: 0 };
  useSeo({
    title: p ? `${pName} — Buy Online at ${inr(pricing.price)} | Soonyoung` : undefined,
    description: p ? `Buy ${pName} online in India at the best price ${inr(pricing.price)} — 100% original ${pBrand ? `${pBrand} ` : ""}beauty product from Soonyoung. Genuine branded cosmetics with delivery across Mumbai, Delhi, Chennai, Bengaluru, Hyderabad & all India.` : undefined,
    path: `/product/${productId}`,
    image: p ? productImage(p) : undefined,
    jsonLd: p ? {
      "@context": "https://schema.org",
      "@type": "Product",
      name: pName,
      image: [productImage(p)],
      description: p.description || `${pName} — original beauty product at Soonyoung.`,
      brand: { "@type": "Brand", name: pBrand || "Soonyoung" },
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: pricing.price,
        availability: (p.in_stock ?? totalStock(p) > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        url: `${window.location.origin}/product/${productId}`,
      },
    } : undefined,
  });

  if (notFound) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center justify-center px-4 pt-28 text-center" data-testid="product-not-found">
        <p className="font-display text-3xl text-cream">This ritual has sold out of existence</p>
        <Link to="/shop" className="mt-6 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest">Back to the shelf</Link>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="mx-auto grid max-w-7xl gap-10 px-4 pt-32 sm:px-6 lg:grid-cols-2" data-testid="product-loading">
        <div className="aspect-[4/5] animate-pulse rounded-3xl bg-moss" />
        <div className="space-y-4 pt-6"><div className="h-8 w-2/3 animate-pulse rounded bg-moss" /><div className="h-5 w-1/3 animate-pulse rounded bg-moss" /><div className="h-24 animate-pulse rounded bg-moss" /></div>
      </div>
    );
  }

  const stock = p.total_stock ?? totalStock(p);
  const inStock = p.in_stock ?? stock > 0;
  const wished = wishlist.ids.includes(p.id);
  const { price, listPrice, mrp, off, offer } = pricing;
  const images = [...new Set([...(p.images || []), p.primary_image, ...(p.additional_images || [])].filter(Boolean))];
  const gallery = images.length ? images : [productImage(p)];

  return (
    <section data-testid="product-page" className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-fern">
        <Link to="/" className="hover:text-gold">Home</Link> / <Link to="/shop" className="hover:text-gold">Shop</Link> / <span className="text-gold">{p.name}</span>
      </p>

      <div className="mt-8 grid gap-10 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="overflow-hidden rounded-3xl border border-champagne/40 bg-moss">
            <img data-testid="product-image" src={gallery[imgIdx]} onError={(e) => { if (!e.currentTarget.dataset.fbk) { e.currentTarget.dataset.fbk = "1"; e.currentTarget.src = FALLBACK_IMG; } }} alt={p.name} className="aspect-[4/5] w-full object-cover" />
          </div>
          {gallery.length > 1 && (
          <div className="mt-3 flex gap-3 overflow-x-auto no-scrollbar">
            {gallery.map((src, i) => (
              <button
                key={i}
                data-testid={`product-thumb-${i}`}
                onClick={() => setImgIdx(i)}
                className={`h-20 w-16 shrink-0 overflow-hidden rounded-xl border-2 transition-colors ${i === imgIdx ? "border-gold" : "border-champagne/40"}`}
              >
                <img src={src} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">{brandMap[p.brand_id] || "Soonyoung Select"}</p>
          <h1 className="mt-2 font-display text-3xl leading-tight tracking-tight text-cream sm:text-5xl">{p.name}</h1>

          <div className="mt-5 flex flex-wrap items-center gap-4">
            {listPrice > price && (
              <span className="text-lg text-fern line-through">{inr(listPrice)}</span>
            )}
            <div className="flex flex-col">
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">{offer ? "Offer price" : "Price"}</span>
              <span data-testid="product-price" className="font-display text-4xl font-bold text-gold">{inr(price)}</span>
            </div>
            {off > 0 && (
              <span className="rounded-full bg-black px-3 py-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-white shadow-md">Offer {off}%</span>
            )}
          </div>
          {mrp > 0 && <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-fern">MRP {inr(mrp)}</p>}
          {offer && (
            <div className="mt-3 inline-flex flex-wrap items-center gap-3 rounded-2xl border border-champagne/50 bg-moss/60 px-4 py-2.5" data-testid="pdp-offer-banner">
              <span className="rounded-full bg-black px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-white">{offerLabel(offer)}</span>
              {offer.name && String(offer.name).replace(/[^\d.]/g, "") !== String(offer.discount_value) && (
                <span className="text-xs font-bold text-cream">{offer.name}</span>
              )}
            </div>
          )}
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-fern">Inclusive of {p.gst_percent ?? 18}% GST</p>

          <p data-testid="product-stock" className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] ${inStock ? "bg-moss text-cream" : "bg-gold/15 text-gold"}`}>
            {inStock ? (stock > 5 ? "In stock" : `Only ${stock} left`) : "Out of stock"}
          </p>

          {p.description && <p className="mt-6 max-w-lg text-sm leading-relaxed text-sage">{p.description}</p>}

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-4 rounded-full border border-champagne/50 px-4 py-3">
              <button data-testid="pdp-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="text-sage hover:text-gold" aria-label="Decrease quantity"><Minus className="h-4 w-4" /></button>
              <span data-testid="pdp-qty-value" className="w-6 text-center font-mono text-sm font-bold text-cream">{qty}</span>
              <button data-testid="pdp-qty-inc" onClick={() => setQty((q) => Math.min(stock || 10, q + 1))} className="text-sage hover:text-gold" aria-label="Increase quantity"><Plus className="h-4 w-4" /></button>
            </div>
            <button
              data-testid="pdp-add-to-cart-btn"
              disabled={!inStock}
              onClick={() => requireAuth(() => addToCart(p.id, qty))}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-forest transition-transform hover:scale-[1.02] disabled:opacity-50 sm:flex-none"
            >
              <ShoppingBag className="h-4 w-4" /> Add to Bag
            </button>
            <button
              data-testid="pdp-wishlist-btn"
              onClick={() => requireAuth(() => toggleWishlist(p.id))}
              className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-champagne/50 text-cream transition-colors hover:border-gold"
              aria-label="Toggle wishlist"
            >
              <Heart className={`h-5 w-5 ${wished ? "fill-gold text-gold" : ""}`} />
            </button>
          </div>

          <div className="mt-8 space-y-3 rounded-2xl border border-champagne/40 bg-pine p-5 text-xs text-sage">
            <p className="flex items-center gap-2"><Truck className="h-4 w-4 text-gold" />
              {pincodeInfo?.serviceable
                ? <>Delivering to <b className="text-cream">{pincodeInfo.pincode}</b> via {pincodeInfo.store_name}</>
                : "Enter your pincode in the header to check delivery"}
            </p>
            <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> 100% authentic · sourced from brand-authorized stock</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fern">SKU {p.sku} · HSN {p.hsn_code || "—"}</p>
            {(() => {
              const sh = shippingInfo(p);
              return (
                <>
                  {!sh.estimated && sh.grams > 0 && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fern" data-testid="pdp-net-weight">
                      Net weight · {sh.grams >= 1000 ? `${sh.kg} kg` : `${sh.grams} g`}
                    </p>
                  )}
                  {!sh.estimated && sh.hasDims && (
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fern" data-testid="pdp-dimensions">
                      Dimensions · {sh.dims[0]} × {sh.dims[1]} × {sh.dims[2]} cm
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </motion.div>
      </div>

      {p.related_products?.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display text-2xl tracking-tight text-cream sm:text-3xl">Pairs beautifully with</h2>
          <div className="mt-6 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 no-scrollbar" data-testid="related-products-rail">
            {p.related_products.map((rp) => (
              <ProductCard key={rp.id} p={cardProduct(rp, brandMap[rp.brand_id])} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
